<template>
  <div class="logic-tab">
    <p class="text-muted mb-3">
      Define conditional logic rules to show, hide, require, or disable this component based on other field values.
    </p>

    <div v-for="(rule, ruleIndex) in component.logic" :key="ruleIndex" class="card mb-3">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="mb-0">Rule {{ ruleIndex + 1 }}</h6>
          <button type="button" class="btn btn-sm btn-outline-danger" @click="removeRule(ruleIndex)">
            Remove Rule
          </button>
        </div>

        <div class="mb-3">
          <label class="form-label">Action</label>
          <select
            class="form-select"
            :value="rule.action"
            @change="updateRule(ruleIndex, 'action', ($event.target as HTMLSelectElement).value)"
          >
            <option value="show">Show</option>
            <option value="hide">Hide</option>
            <option value="require">Make Required</option>
            <option value="disable">Disable</option>
          </select>
        </div>

        <div class="mb-3">
          <label class="form-label">When</label>
          <select
            class="form-select"
            :value="rule.conjunction"
            @change="updateRule(ruleIndex, 'conjunction', ($event.target as HTMLSelectElement).value)"
          >
            <option value="and">All conditions match (AND)</option>
            <option value="or">Any condition matches (OR)</option>
          </select>
        </div>

        <div class="mb-3">
          <label class="form-label fw-bold">Conditions</label>
          <div
            v-for="(condition, condIndex) in rule.conditions"
            :key="condIndex"
            class="border rounded p-2 mb-2"
          >
            <div class="row g-2">
              <div class="col-md-4">
                <input
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Field ID"
                  :value="condition.field"
                  @input="updateCondition(ruleIndex, condIndex, 'field', ($event.target as HTMLInputElement).value)"
                />
              </div>
              <div class="col-md-3">
                <select
                  class="form-select form-select-sm"
                  :value="condition.operator"
                  @change="updateCondition(ruleIndex, condIndex, 'operator', ($event.target as HTMLSelectElement).value)"
                >
                  <option value="equals">Equals</option>
                  <option value="notEquals">Not Equals</option>
                  <option value="contains">Contains</option>
                  <option value="greaterThan">Greater Than</option>
                  <option value="lessThan">Less Than</option>
                </select>
              </div>
              <div class="col-md-4">
                <input
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Value"
                  :value="condition.value"
                  @input="updateCondition(ruleIndex, condIndex, 'value', ($event.target as HTMLInputElement).value)"
                />
              </div>
              <div class="col-md-1">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger w-100"
                  @click="removeCondition(ruleIndex, condIndex)"
                >
                  ×
                </button>
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn btn-sm btn-outline-primary mt-2"
            @click="addCondition(ruleIndex)"
          >
            Add Condition
          </button>
        </div>
      </div>
    </div>

    <button type="button" class="btn btn-outline-primary" @click="addRule">
      Add Logic Rule
    </button>
  </div>
</template>

<script setup lang="ts">
import type { FormComponent, LogicRule, LogicCondition } from '../../types/form-builder';

interface Props {
  component: FormComponent;
}

interface Emits {
  (e: 'update', updates: Partial<FormComponent>): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

function addRule() {
  const newRule: LogicRule = {
    action: 'show',
    conjunction: 'and',
    conditions: [
      {
        field: '',
        operator: 'equals',
        value: '',
      },
    ],
  };

  emit('update', {
    logic: [...props.component.logic, newRule],
  });
}

function removeRule(ruleIndex: number) {
  const logic = [...props.component.logic];
  logic.splice(ruleIndex, 1);
  emit('update', { logic });
}

function updateRule(ruleIndex: number, field: keyof LogicRule, value: any) {
  const logic = [...props.component.logic];
  logic[ruleIndex] = { ...logic[ruleIndex], [field]: value };
  emit('update', { logic });
}

function addCondition(ruleIndex: number) {
  const logic = [...props.component.logic];
  const newCondition: LogicCondition = {
    field: '',
    operator: 'equals',
    value: '',
  };

  logic[ruleIndex] = {
    ...logic[ruleIndex],
    conditions: [...logic[ruleIndex].conditions, newCondition],
  };

  emit('update', { logic });
}

function removeCondition(ruleIndex: number, condIndex: number) {
  const logic = [...props.component.logic];
  logic[ruleIndex].conditions.splice(condIndex, 1);
  emit('update', { logic });
}

function updateCondition(
  ruleIndex: number,
  condIndex: number,
  field: keyof LogicCondition,
  value: any
) {
  const logic = [...props.component.logic];
  logic[ruleIndex].conditions[condIndex] = {
    ...logic[ruleIndex].conditions[condIndex],
    [field]: value,
  };
  emit('update', { logic });
}
</script>
